import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
	getCurrentPartStatus, 
	shouldRedirectBasedOnOperation,
	getNextOperation 
} from '../../utils/operationFlow';
import { OPERATIONS, REDIRECT_URLS } from '../../constants';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

/**
 * OperationFlowManager - Handles automatic routing based on operation completion
 * This component should be used in pages that need operation-based flow management
 */
const OperationFlowManager = ({
	children,
	allowedOperations = [],
	currentLineItem = {},
	updateCurrentLineItem,
	updateOperations,
	workStreamType,
	user,
	// Optional props for customization
	enableAutoRedirect = true,
	showLoadingOnRedirect = true,
	onOperationChange = null,
}) => {
	const navigate = useNavigate();
	const location = useLocation();
	const [isCheckingFlow, setIsCheckingFlow] = useState(false);
	const [currentPart, setCurrentPart] = useState({});

	/**
	 * Check current operation status and redirect if needed
	 */
	const checkOperationFlow = async () => {
		if (!enableAutoRedirect || !allowedOperations.length) {
			return;
		}

		setIsCheckingFlow(true);
		try {
			// Get current part status from API
			const partData = await getCurrentPartStatus();
			setCurrentPart(partData);

			// If API returns empty response, start with first operation
			if (!partData || !Object.keys(partData).length) {
				const firstOperation = allowedOperations[0];
				const firstPath = REDIRECT_URLS[firstOperation] || '/scan-vin';
				
				updateOperations({ currentOperation: firstOperation });
				
				if (onOperationChange) {
					onOperationChange(firstOperation, firstPath);
				}
				
				// Only navigate if we're not already on the correct path
				if (location.pathname !== firstPath) {
					navigate(firstPath);
				}
				return;
			}

			// Check if redirect is needed
			const redirectInfo = shouldRedirectBasedOnOperation(
				partData,
				allowedOperations,
				location.pathname
			);

			if (redirectInfo.shouldRedirect) {
				// Update operations state
				updateOperations({ currentOperation: redirectInfo.operation });
				
				// Call operation change callback if provided
				if (onOperationChange) {
					onOperationChange(redirectInfo.operation, redirectInfo.targetPath);
				}
				
				// Navigate to correct screen
				navigate(redirectInfo.targetPath);
			}
		} catch (error) {
			console.error('Error checking operation flow:', error);
			// On error, default to first operation
			const firstOperation = allowedOperations[0];
			const firstPath = REDIRECT_URLS[firstOperation] || '/scan-vin';
			
			updateOperations({ currentOperation: firstOperation });
			
			if (location.pathname !== firstPath) {
				navigate(firstPath);
			}
		} finally {
			setIsCheckingFlow(false);
		}
	};

	/**
	 * Handle operation completion
	 * @param {string} completedOperation - The operation that was completed
	 * @param {Function} onSuccess - Success callback
	 * @param {Function} onError - Error callback
	 */
	const handleOperationCompletion = async (completedOperation, onSuccess, onError) => {
		try {
			// Update current line item with completed operation
			const completedOperations = currentLineItem?.completedOperations || [];
			const updatedCompletedOperations = [...completedOperations, completedOperation];
			
			const updatedLineItem = {
				...currentLineItem,
				completedOperations: updatedCompletedOperations
			};
			
			updateCurrentLineItem(updatedLineItem);
			
			// Determine next operation
			const nextOperation = getNextOperation(updatedCompletedOperations, allowedOperations);
			updateOperations({ currentOperation: nextOperation });
			
			// Navigate to next operation screen
			const nextPath = REDIRECT_URLS[nextOperation] || '/scan-vin';
			
			// Call operation change callback if provided
			if (onOperationChange) {
				onOperationChange(nextOperation, nextPath);
			}
			
			navigate(nextPath);
			
			if (onSuccess) {
				onSuccess({
					completedOperation,
					nextOperation,
					nextPath,
					updatedLineItem
				});
			}
		} catch (error) {
			console.error('Error handling operation completion:', error);
			if (onError) {
				onError(error);
			}
		}
	};

	/**
	 * Get current operation based on path
	 */
	const getCurrentOperation = () => {
		const pathToOperation = {
			'/scan-vin': OPERATIONS.RECEIVE_SN,
			'/assembly/receive-part': OPERATIONS.RECEIVE_PART,
			'/assembly/part-validation': OPERATIONS.PART_VALIDATION,
			'/assembly/process-validation': OPERATIONS.PROCESS_VALIDATION,
			'/assembly/book': OPERATIONS.BOOK,
			'/assembly/engraving': OPERATIONS.ENGRAVING,
			// '/assembly/machine': OPERATIONS.MACHINE,
			'/assembly/take-in-out': OPERATIONS.TAKE_IN_OUT,
		};
		
		return pathToOperation[location.pathname] || OPERATIONS.RECEIVE_SN;
	};

	/**
	 * Check if user has permission for current operation
	 */
	const hasPermission = (operation) => {
		return allowedOperations.includes(operation);
	};

	/**
	 * Refresh current part data
	 */
	const refreshCurrentPart = async () => {
		try {
			const partData = await getCurrentPartStatus();
			setCurrentPart(partData);
			updateCurrentLineItem(partData);
			return partData;
		} catch (error) {
			console.error('Error refreshing current part:', error);
			return {};
		}
	};

	// Prevent infinite loops by adding a ref to track if we've already checked
	const hasCheckedFlow = React.useRef(false);
	
	// Check operation flow on mount and when dependencies change
	useEffect(() => {
		if (allowedOperations.length > 0 && !hasCheckedFlow.current && !isCheckingFlow) {
			hasCheckedFlow.current = true;
			checkOperationFlow();
		}
	}, [allowedOperations]);

	// Reset the check flag when pathname changes to allow re-checking
	useEffect(() => {
		hasCheckedFlow.current = false;
	}, [location.pathname]);

	// Provide operation flow utilities to children via context
	const operationFlowContext = {
		handleOperationCompletion,
		getCurrentOperation,
		hasPermission,
		refreshCurrentPart,
		checkOperationFlow,
		currentPart,
		isCheckingFlow,
		allowedOperations,
		currentLineItem,
	};

	// Show loading spinner if checking flow and enabled
	if (isCheckingFlow && showLoadingOnRedirect) {
		return <LoadingSpinner variant="overlay" text="Checking operation flow..." />;
	}

	// Render children with operation flow context
	return React.cloneElement(children, { operationFlow: operationFlowContext });
};

// Redux connector
const mapStateToProps = (state) => ({
	allowedOperations: state.operations?.allowedOperations || [],
	currentLineItem: state.station?.currentLineItem || {},
	workStreamType: state.user?.workStreamType,
	user: state.user,
});

const mapDispatchToProps = (dispatch) => ({
	updateCurrentLineItem: (data) => dispatch({ type: 'UPDATE_CURRENT_LINE_ITEM', payload: data }),
	updateOperations: (data) => dispatch({ type: 'UPDATE_OPERATIONS', payload: data }),
});

export default connect(mapStateToProps, mapDispatchToProps)(OperationFlowManager);

/**
 * Higher-order component to wrap pages with operation flow management
 */
export const withOperationFlow = (Component, options = {}) => {
	return (props) => (
		<OperationFlowManager {...options}>
			<Component {...props} />
		</OperationFlowManager>
	);
};
